/* eslint-disable no-console, import/prefer-default-export */
import * as changePassword from './change-password';
import { formatPhone } from '../../utils/form-validation-utils';

const rootSelector = '.js-account-info';
const viewSelector = '.js-account-info-view';
const editPanelSelector = '.js-account-info-edit-panel';
const fieldSelector = '.js-account-field';
const editingClass = 'is-account-editing';
const desktopMq = window.matchMedia('(min-width: 1200px)');
const tokenReloadFlag = 'accountTokenReloaded';

let fieldSnapshot = {};
let isWired = false;

const getRoot = () => document.querySelector(rootSelector);

const isDesktopLayout = () => desktopMq.matches;

const isEditVisible = (root) => {
  const edit = root && root.querySelector(editPanelSelector);
  return Boolean(edit && !edit.classList.contains('d-none'));
};

const lockMainColumn = (root) => {
  if (!root || !isDesktopLayout()) {
    return;
  }
  document.body.classList.add(editingClass);
  window.requestAnimationFrame(() => {
    root.scrollIntoView({ block: 'start' });
  });
};

const unlockMainColumn = () => {
  document.body.classList.remove(editingClass);
};

const syncMainColumnLock = (root) => {
  if (isEditVisible(root) && isDesktopLayout()) {
    lockMainColumn(root);
    return;
  }
  unlockMainColumn();
};

const formatPhoneField = (root) => {
  const phoneInput = root.querySelector('#Account_PhoneNumber');
  if (phoneInput) {
    phoneInput.value = formatPhone(phoneInput.value);
  }
};

const snapshotFields = (root) => {
  fieldSnapshot = {};
  root.querySelectorAll(fieldSelector).forEach((field) => {
    const key = field.getAttribute('name') || field.getAttribute('data-field');
    if (key) {
      fieldSnapshot[key] = field.value;
    }
  });
};

const restoreFields = (root) => {
  root.querySelectorAll(fieldSelector).forEach((field) => {
    const key = field.getAttribute('name') || field.getAttribute('data-field');
    if (key && Object.prototype.hasOwnProperty.call(fieldSnapshot, key)) {
      field.value = fieldSnapshot[key];
    }
  });
  formatPhoneField(root);
};

const showEdit = (root) => {
  const view = root.querySelector(viewSelector);
  const edit = root.querySelector(editPanelSelector);
  if (view) {
    view.classList.add('d-none');
  }
  if (edit) {
    edit.classList.remove('d-none');
  }
  snapshotFields(root);
  formatPhoneField(root);
  window.requestAnimationFrame(() => {
    lockMainColumn(root);
  });
};

const showView = (root) => {
  const view = root.querySelector(viewSelector);
  const edit = root.querySelector(editPanelSelector);
  const passwordPanel = root.querySelector('.change-your-password');
  if (edit) {
    edit.classList.add('d-none');
  }
  if (view) {
    view.classList.remove('d-none');
  }
  if (passwordPanel) {
    changePassword.hideChangePasswordModule();
    passwordPanel.classList.add('hide');
  }
  unlockMainColumn();
};

const hideStatusMessage = (root) => {
  const statusMsg = root.querySelector('.form-status-message');
  if (!statusMsg) {
    return;
  }
  statusMsg.classList.add('hidden');
  statusMsg.classList.remove('c-infobox--fade');
};

const scheduleStatusHide = (root) => {
  const statusMsg = root.querySelector('.form-status-message');
  if (statusMsg && statusMsg.innerHTML.trim() !== '') {
    setTimeout(() => hideStatusMessage(root), 4000);
  }
};

const enhanceRoot = (root) => {
  if (!root) {
    return;
  }
  formatPhoneField(root);
  scheduleStatusHide(root);
  const editPanel = root.querySelector(editPanelSelector);
  if (editPanel && !editPanel.classList.contains('d-none')) {
    snapshotFields(root);
  }
  const passwordPanel = root.querySelector('.change-your-password');
  if (passwordPanel && !passwordPanel.classList.contains('hide')) {
    changePassword.showChangePasswordModule();
  }
  syncMainColumnLock(root);
};

const replaceRootHtml = (html) => {
  const root = getRoot();
  if (!root) {
    return;
  }
  const wrapper = document.createElement('div');
  wrapper.innerHTML = html.trim();
  const next = wrapper.firstElementChild;
  if (!next) {
    return;
  }
  root.parentNode.replaceChild(next, root);
  enhanceRoot(next);

  if (!isEditVisible(next)) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

const postForm = (form, url) => {
  const root = getRoot();
  const pageId = root ? root.getAttribute('data-page-id') : '';
  const body = new URLSearchParams(new FormData(form));
  if (pageId && !body.has('pageId')) {
    body.append('pageId', pageId);
  }

  const submitButtons = form.querySelectorAll('button[type="submit"], input[type="submit"]');
  submitButtons.forEach((button) => { button.disabled = true; });

  return fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    body,
  }).then((response) => {
    if (response.status === 419) {
      if (!window.sessionStorage.getItem(tokenReloadFlag)) {
        window.sessionStorage.setItem(tokenReloadFlag, '1');
        window.location.reload();
      }
      throw new Error('Anti-forgery token is stale');
    }
    if (!response.ok) {
      throw new Error(`Account info request failed with status ${response.status}`);
    }
    return response.text();
  }).then((html) => {
    replaceRootHtml(html);
  }).catch((error) => {
    console.error(error);
    submitButtons.forEach((button) => { button.disabled = false; });
  });
};

const onClick = (event) => {
  const root = event.target.closest(rootSelector);
  if (!root) {
    return;
  }

  const editBtn = event.target.closest('.js-account-info-edit');
  if (editBtn && root.contains(editBtn)) {
    event.preventDefault();
    showEdit(root);
    return;
  }

  const cancelBtn = event.target.closest('.js-account-info-cancel');
  if (cancelBtn && root.contains(cancelBtn)) {
    event.preventDefault();
    restoreFields(root);
    showView(root);
    return;
  }

  const changePasswordBtn = event.target.closest('.btn--change-password');
  if (changePasswordBtn && root.contains(changePasswordBtn)) {
    event.preventDefault();
    const passwordPanel = root.querySelector('.change-your-password');
    if (passwordPanel) {
      passwordPanel.classList.remove('hide');
      changePassword.showChangePasswordModule();
    }
    return;
  }

  const cancelPasswordBtn = event.target.closest('.btn--cancel-change-password');
  if (cancelPasswordBtn && root.contains(cancelPasswordBtn)) {
    event.preventDefault();
    const passwordPanel = root.querySelector('.change-your-password');
    changePassword.hideChangePasswordModule();
    if (passwordPanel) {
      passwordPanel.classList.add('hide');
    }
  }
};

const onSubmit = (event) => {
  const root = event.target.closest(rootSelector);
  if (!root) {
    return;
  }

  const profileForm = event.target.closest('.js-account-profile-form');
  if (profileForm && root.contains(profileForm)) {
    event.preventDefault();
    const url = root.getAttribute('data-update-url') || profileForm.getAttribute('action');
    postForm(profileForm, url);
    return;
  }

  const passwordForm = event.target.closest('.js-account-password-form');
  if (passwordForm && root.contains(passwordForm)) {
    event.preventDefault();
    const url = root.getAttribute('data-password-url') || passwordForm.getAttribute('action');
    postForm(passwordForm, url);
  }
};

const init = () => {
  if (!getRoot()) {
    return;
  }
  if (!isWired) {
    document.addEventListener('click', onClick);
    document.addEventListener('submit', onSubmit);
    desktopMq.addEventListener('change', () => {
      syncMainColumnLock(getRoot());
    });
    isWired = true;
  }
  enhanceRoot(getRoot());
};

export {
  init,
};
/* eslint-enable no-console, import/prefer-default-export */
